import React, { useState } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Stepper,
  Step,
  StepLabel,
  Button,
  TextField,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { 
  PhoneAuthProvider, 
  PhoneMultiFactorGenerator, 
  multiFactor,
  EmailAuthProvider,
  reauthenticateWithCredential
} from 'firebase/auth';
import { getRecaptchaVerifier } from '../../utilities/authUtils';
import { useFirebase } from '../../contexts/FirebaseContext';

const steps = [
  'Prerequisites Check',
  'Reauthentication',
  'Send Verification Code',
  'Verify and Enable'
];

export const MfaSetup = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { auth } = useFirebase();
  
  // Step tracking
  const [activeStep, setActiveStep] = useState(0);
  
  // Reauthentication state
  const [reauthEmail, setReauthEmail] = useState("");
  const [reauthPassword, setReauthPassword] = useState("");
  const [showReauthDialog, setShowReauthDialog] = useState(false);
  
  // Verification state
  const [verificationId, setVerificationId] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  
  // UI state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Check prerequisites
  const checkPrerequisites = () => {
    if (!currentUser?.emailVerified) {
      setError("Please verify your email address before enabling 2FA");
      return false;
    }
    if (!currentUser?.phoneNumber) {
      setError("Please link a phone number before enabling 2FA");
      return false;
    }
    return true;
  };

  // Handle reauthentication
  const handleReauthenticate = async () => {
    try {
      setLoading(true);
      setError(null);

      const credential = EmailAuthProvider.credential(reauthEmail, reauthPassword);
      await reauthenticateWithCredential(currentUser, credential);
      
      // After successful re-authentication
      setShowReauthDialog(false);
      setReauthEmail("");
      setReauthPassword("");
      
      // Refresh the user object and move to next step
      await currentUser.reload();
      setActiveStep(2);
      await startVerification();
      
    } catch (error) {
      console.error("Re-authentication error:", error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  // Start verification process
  const startVerification = async () => {
    let recaptchaVerifier = null;
    
    try {
      setLoading(true);
      setError(null);
      
      // Get or create a RecaptchaVerifier instance
      const containerId = `recaptcha-container-mfa-${Date.now()}`;
      recaptchaVerifier = await getRecaptchaVerifier(auth, containerId);
      
      // Get multi-factor session
      const multiFactorUser = multiFactor(currentUser);
      const multiFactorSession = await multiFactorUser.getSession();
      
      // Specify the phone number and pass the MFA session
      const phoneInfoOptions = {
        phoneNumber: currentUser.phoneNumber,
        session: multiFactorSession
      };
      
      // Create phone auth provider and send verification code
      const phoneAuthProvider = new PhoneAuthProvider(auth);
      const verificationId = await phoneAuthProvider.verifyPhoneNumber(
        phoneInfoOptions, 
        recaptchaVerifier
      );
      
      // Store verification ID and move to next step
      setVerificationId(verificationId);
      setActiveStep(3);
      setSuccess("Verification code sent successfully");
      
    } catch (error) {
      console.error("Error starting verification:", error);
      setError(error.message);
      if (error.code === 'auth/requires-recent-login') {
        setShowReauthDialog(true);
      }
    } finally {
      if (recaptchaVerifier?.clear) {
        await recaptchaVerifier.clear();
      }
      setLoading(false);
    }
  };

  // Complete MFA setup
  const completeSetup = async () => {
    try {
      setLoading(true);
      setError(null);

      if (!verificationCode) {
        setError("Please enter the verification code");
        return;
      }

      // Create credential with verification ID and code
      const cred = PhoneAuthProvider.credential(verificationId, verificationCode);
      
      // Create multi-factor assertion
      const multiFactorAssertion = PhoneMultiFactorGenerator.assertion(cred);
      
      // Complete enrollment
      const multiFactorUser = multiFactor(currentUser);
      await multiFactorUser.enroll(multiFactorAssertion, "Phone Number");
      
      // Refresh the user object to ensure MFA status is updated
      await currentUser.reload();
      
      setSuccess("Two-factor authentication has been successfully enabled");
      
      // Navigate back to settings after a short delay
      setTimeout(() => {
        navigate('/settings', { 
          state: { 
            message: "Two-factor authentication has been successfully enabled",
            mfaEnabled: true
          }
        });
      }, 1500);
      
    } catch (error) {
      console.error("Error completing MFA setup:", error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  // Handle step navigation
  const handleNext = async () => {
    switch (activeStep) {
      case 0:
        if (checkPrerequisites()) {
          setActiveStep(1);
          setShowReauthDialog(true);
        }
        break;
      case 1:
        // Handled by handleReauthenticate
        break;
      case 2:
        // Handled by startVerification
        break;
      case 3:
        await completeSetup();
        break;
      default:
        break;
    }
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  return (
    <Container maxWidth="md">
      <Paper elevation={3} sx={{ p: 4, mt: 4, mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          Set Up Two-Factor Authentication
        </Typography>

        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        <Box>
          {activeStep === 3 && (
            <Box>
              <Typography variant="body1" sx={{ mb: 2 }}>
                Enter the verification code sent to {currentUser?.phoneNumber}:
              </Typography>
              <TextField
                margin="normal"
                required
                fullWidth
                id="verificationCode"
                label="Verification Code"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value)}
                disabled={loading}
              />
            </Box>
          )}

          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
            <Button
              onClick={() => navigate('/settings')}
              disabled={loading}
            >
              Cancel
            </Button>
            <Box>
              <Button
                disabled={activeStep === 0 || loading}
                onClick={handleBack}
                sx={{ mr: 1 }}
              >
                Back
              </Button>
              <Button
                variant="contained"
                onClick={handleNext}
                disabled={loading}
              >
                {loading ? <CircularProgress size={24} /> : activeStep === steps.length - 1 ? 'Complete' : 'Next'}
              </Button>
            </Box>
          </Box>
        </Box>

        {/* Reauthentication Dialog */}
        <Dialog open={showReauthDialog} onClose={() => !loading && setShowReauthDialog(false)}>
          <DialogTitle>Reauthentication Required</DialogTitle>
          <DialogContent>
            <DialogContentText>
              For security reasons, please re-enter your credentials to continue.
            </DialogContentText>
            <TextField
              margin="normal"
              required
              fullWidth
              id="reauthEmail"
              label="Email Address"
              name="email"
              autoComplete="email"
              value={reauthEmail}
              onChange={(e) => setReauthEmail(e.target.value)}
              disabled={loading}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Password"
              type="password"
              id="reauthPassword"
              autoComplete="current-password"
              value={reauthPassword}
              onChange={(e) => setReauthPassword(e.target.value)}
              disabled={loading}
            />
          </DialogContent>
          <DialogActions>
            <Button 
              onClick={() => {
                setShowReauthDialog(false);
                setActiveStep(0);
              }} 
              disabled={loading}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleReauthenticate}
              disabled={loading || !reauthEmail || !reauthPassword}
              variant="contained"
            >
              {loading ? <CircularProgress size={24} /> : 'Authenticate'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Recaptcha Container */}
        <div id={`recaptcha-container-mfa-${Date.now()}`} />
      </Paper>
    </Container>
  );
}; 